/**
 * Certificates and despatch notes for the lines on a shipment.
 *
 * The documents are generated per PO line — a certificate covers the parts a
 * line's jobs produced — while a shipment usually carries several lines. So
 * this lists the lines rather than offering one pair of buttons that would
 * have to guess which line was meant.
 *
 * Lines whose jobs have not been imported are left out entirely: their
 * buttons would return an error the user can do nothing about.
 */
import { useEffect, useState } from 'react';
import { FileBadge } from 'lucide-react';
import { purchasingAPI } from '../../lib/api';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import JobDocumentButtons from './JobDocumentButtons';
import type { ASNDeliveryLineDetail, ASNScheduleDetail } from './asnModalTypes';

interface ASNJobDocumentsProps {
  /** Schedule-based shipment rows. Empty on delivery-line shipments. */
  schedules: ASNScheduleDetail[];
  /**
   * Delivery-line shipment rows.
   *
   * A shipment reaches its PO lines by one route or the other depending on
   * how it was raised — ERP delivery-line feeds produce no schedules at all —
   * so both are read here. Reading only schedules left this panel invisible
   * for every shipment on an ERP-fed site.
   */
  deliveryLines: ASNDeliveryLineDetail[];
}

interface DocumentLine {
  lineId: number;
  label: string;
  partNumber: string;
}

/**
 * Collapse the shipment's rows to one entry per PO line.
 *
 * A line can appear on several schedule rows; its documents cover the line,
 * so listing it once per row would offer the same certificate repeatedly.
 */
function toDocumentLines(
  schedules: ASNScheduleDetail[],
  deliveryLines: ASNDeliveryLineDetail[],
): DocumentLine[] {
  const byLineId = new Map<number, DocumentLine>();

  const add = (lineId: number | null | undefined, label: string, partNumber: string) => {
    if (!lineId || byLineId.has(lineId)) return;
    byLineId.set(lineId, { lineId, label: label || `Line ${lineId}`, partNumber });
  };

  for (const schedule of schedules) {
    const poNumber = schedule.po_number || schedule.po_line_details?.po_details?.po_number || '';
    const lineNumber = schedule.line_number ?? schedule.po_line_details?.line_number;
    add(
      schedule.po_line_id,
      [poNumber && `PO ${poNumber}`, lineNumber != null && `Line ${lineNumber}`]
        .filter(Boolean).join(' · '),
      schedule.part_number || schedule.po_line_details?.part_number || '',
    );
  }

  for (const line of deliveryLines) {
    add(
      line.po_line_id,
      [line.po_order_number && `PO ${line.po_order_number}`,
       line.po_line_number && `Line ${line.po_line_number}`]
        .filter(Boolean).join(' · '),
      line.part_number || '',
    );
  }

  return Array.from(byLineId.values());
}

/**
 * Per-line certificate and despatch note buttons for a shipment.
 *
 * @param schedules - schedule-based rows
 * @param deliveryLines - delivery-line rows
 */
export default function ASNJobDocuments({ schedules, deliveryLines }: ASNJobDocumentsProps) {
  // Same gate as the order-line Documents tab. The backend refuses to
  // generate either document while the switch is off, so without this the
  // shipment view would offer buttons that can only return an error.
  const { isFeatureEnabled } = useMenuSettings();
  const documentsEnabled = isFeatureEnabled('job_documents');
  const [lineIdsWithJobs, setLineIdsWithJobs] = useState<number[] | null>(null);
  const lines = toDocumentLines(schedules, deliveryLines);
  // Stable across renders so the effect does not refire on a new array of the
  // same ids each time the parent re-renders.
  const idKey = lines.map(l => l.lineId).join(',');

  useEffect(() => {
    const ids = idKey ? idKey.split(',').map(Number) : [];
    if (ids.length === 0) {
      setLineIdsWithJobs([]);
      return;
    }
    let cancelled = false;
    purchasingAPI.getLinesWithJobs(ids)
      .then(res => { if (!cancelled) setLineIdsWithJobs(res.data); })
      // Never fatal: the uploaded-documents table below is the tab's main job.
      .catch(() => { if (!cancelled) setLineIdsWithJobs([]); });
    return () => { cancelled = true; };
  }, [idKey]);

  if (!documentsEnabled) return null;
  if (lineIdsWithJobs === null) return null;

  const printable = lines.filter(line => lineIdsWithJobs.includes(line.lineId));
  if (printable.length === 0) return null;

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/30 border-b">
        <FileBadge className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <div className="text-sm font-medium">Certificates &amp; Despatch Notes</div>
        <div className="text-xs text-muted-foreground ml-auto">
          Generated from ERP job records
        </div>
      </div>
      {printable.map(line => (
        <div
          key={line.lineId}
          className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium">{line.label}</div>
            {line.partNumber && (
              <div className="font-mono text-xs text-muted-foreground">{line.partNumber}</div>
            )}
          </div>
          <JobDocumentButtons lineId={line.lineId} />
        </div>
      ))}
    </div>
  );
}
